import type { ApiResponse } from '@shared/types'
import type { CountryCode } from 'libphonenumber-js'
import type Pubnub from 'pubnub'
import type { useNavigate } from 'router-v6'
import type { AccessDenialReason } from 'v2/pages/misc/access-denied'
import type { usePubNub } from 'v2/shared/hooks/use-pubnub'
import type { StoreApi } from 'zustand'
import type { RUserSession } from './api/auth'
import type { Channels } from './api/conversations'
import type { RWhatsappTemplate } from './api/settings'

/**
 * Represents the global window object.
 * CX is a global object that contains methods for the application.
 * */
declare global {
  interface Window {
    cx: {
      isPrimary: boolean
      logout: (noWarning?: boolean) => void
      pubnubInstance?: Pubnub // TODO: Remove this
      navigate: ReturnType<typeof useNavigate>
      pubnub: {
        publishMutation: ReturnType<typeof usePubNub>['publishMutation']
      }
      tabManager?: {
        isLastActiveTab: () => boolean
        getTabId: () => string
        isLastUsedTab: () => boolean
      }
    }
    contactoCountryData: Array<[string, CountryCode, string, string]>
    // HubSpot tracking-code command queue: [method, ...args] tuples. The array is
    // ours, not the script's — HubSpot drains whatever it finds when it loads, so
    // calls made before (or instead of) the script arriving are simply buffered.
    _hsq?: Array<[string, ...unknown[]]>
    // GTM's command queue, same buffering idea as _hsq: the container drains
    // whatever it finds on load, so the gtm.js start event can be pushed first.
    dataLayer?: Array<Record<string, unknown>>
    posthog?: {
      identify: (distinctId: string, properties?: Record<string, unknown>) => void
      alias: (alias: string, original?: string) => void
      group: (type: string, key: string, properties?: Record<string, unknown>) => void
      reset: () => void
      capture: (
        event: string,
        properties?: Record<string, unknown>,
        options?: { transport?: 'XHR' | 'sendBeacon' | 'fetch' },
      ) => void
      // Optional: the real SDK always has it, but the test stub and a partially
      // blocked posthog-js may not, so call sites use `?.()`.
      stopSessionRecording?: () => void
    }
  }
}

/**
 * Sidebar grouping and display metadata
 */
export enum PlanType {
  FREE_TRIAL = 'free_trial',
  PROFESSIONAL = 'professional',
  ENTERPRISE = 'enterprise',
}

export const PLAN_ORDER = [PlanType.FREE_TRIAL, PlanType.PROFESSIONAL, PlanType.ENTERPRISE]

export enum SidebarGroups {
  aiStudio = 'ai-studio',
  views = 'views',
  inbox = 'inbox',
  queued = 'queued',
  platform = 'platform',
  humanSpecialist = 'human-specialist',
  billing = 'billing',
  settings = 'settings',
  build = 'build',
  deploy = 'deploy',
  monitor = 'monitor',
}
export interface SidebarMeta {
  // Which sidebar group this route belongs to
  group?: SidebarGroups
  // Custom icon for sidebar (overrides main icon)
  sidebarIcon?: React.ReactNode
  // Display order within the group
  order?: number
  // Custom title for sidebar (overrides main title)
  sidebarTitle?: string
  // Whether this route should appear in sidebar
  showInSidebar?: boolean
  // Whether this route should appear in main sidebar vs settings sidebar
  sidebarType?: 'main' | 'settings'
  // Pin this route to the bottom of the main sidebar (above the footer)
  pinnedToBottom?: boolean
  // Whether this can be pinned/unpinned in conversations sidebar
  isPinnable?: boolean
  // Special icon type for custom handling
  iconType?: 'home'
  // Whether to show chevron for special behavior
  hasChevron?: boolean
  // Which onClick handler to use
  onClick?: 'onConversationsClick'
  // Which viewsData key to use for badge count
  badgeKey?: string
}

/**
 * Represents a base route in the application.
 */
interface BaseRoute {
  path: string
  icon?: React.ReactNode
  title: string
  className?: string
  openInNewTab?: boolean
  allowMultipleActiveAccordions?: boolean
  notAllowed?: boolean // if true, the route renders the access-denied page
  // Which gate closed this route, when it is known. Drives the access-denied
  // wording; omit it and the neutral copy is used. `notAllowed` collapses role,
  // plan, region and feature gates into one boolean, so do NOT assume a reason.
  accessDenialReason?: AccessDenialReason
  meta?: Record<string, unknown>
  hasAccess?: boolean // if false, the route renders the access-denied page
  sidebar?: SidebarMeta // Sidebar-specific metadata
  // Enterprise-lock this nav item: ghosted + trailing lock icon in the sidebar,
  // hover tooltip, and clicking opens the upgrade flow instead of navigating.
  locked?: boolean
  lockedTooltip?: string
  onLockedClick?: () => void
}

/**
 * Represents a parent route that contains child routes.
 */
export interface ParentRoute extends BaseRoute {
  children: IRoute[]
}

/**
 * Represents a child route in the application.
 */
interface ChildRoute extends BaseRoute {
  component?: React.ReactNode
  hidden?: boolean
  children?: IRoute[]
  sidebarOnly?: boolean // Routes that are only for sidebar display, not actual routing
}

/**
 * Represents a route in the application.
 */
export type IRoute = ParentRoute | ChildRoute

// Api Service related types
/**
 * This type is used to define the structure of a service that makes a POST request.
 * It takes three generic parameters:
 * - TApiResponse: The type of the response data.
 * - TPayload: The type of the request payload. It is optional and defaults to 'unknown'.
 * - TArgs: The type of the additional arguments. It is optional and defaults to 'unknown[]'.
 * The function takes the request payload and additional arguments as parameters and returns a Promise.
 * The Promise resolves to an ApiResponse object that contains the response data and other metadata.
 * Eg: holdCall: (payload: HoldCallPayload, callUUID: string) => Promise<ApiResponse<null>>
 * Payload is the first and extra args are the rest
 */

export type PostService<TApiResponse, TPayload = unknown, TArgs extends unknown[] = unknown[]> = (
  data: TPayload,
  ...args: TArgs
) => Promise<ApiResponse<TApiResponse>>

export type GetService<TApiResponse, TArgs extends unknown[] = unknown[]> = (
  ...args: TArgs
) => Promise<ApiResponse<TApiResponse>>

export type DeleteService<TApiResponse, TParams = Record<string, string> | string> = (
  params?: TParams,
) => Promise<ApiResponse<TApiResponse>>

export type PutService<TApiResponse, TPayload = unknown, TArgs extends unknown[] = unknown[]> = (
  data: TPayload,
  ...args: TArgs
) => Promise<ApiResponse<TApiResponse>>

export type PatchService<TApiResponse, TPayload = unknown, TArgs extends unknown[] = unknown[]> = (
  data: TPayload,
  ...args: TArgs
) => Promise<ApiResponse<TApiResponse>>

export enum BlockingModalType {
  ACTIVE_CALL_LOGOUT = 'ACTIVE_CALL_LOGOUT', // When there is an active call and user clicks logout
}

// List of BE services
export enum BackendServiceName {
  core = 'contacto-core',
  hodor = 'hodor',
  workflow = 'contacto-workflow',
  uploader = 'contacto-uploader',
  default = '',
  execution = 'contacto-execution',
  externalApps = 'external-apps-service',
  billing = 'billing',
  agentObservability = 'agent-observability',
}

export enum RChannels {
  call = 'call',
  message = 'message',
  whatsapp = 'whatsapp',
}

export type StoreActions<TState, TActions> = (
  set: StoreApi<TState>['setState'],
  get: () => TState,
) => TActions

export enum FlowTypes {
  incoming_call = 'incoming_call',
  outgoing_call = 'outgoing_call',
  incoming_sms = 'incoming_sms',
  outgoing_sms = 'outgoing_sms',
  incoming_chat = 'incoming_chat',
  incoming_whatsapp = 'incoming_whatsapp',
  outgoing_whatsapp = 'outgoing_whatsapp',
  api_request = 'api_request',
  incoming_slack = 'incoming_slack',
}

export type PhloType = FlowTypes | 'omni'

export enum TriggerGroupType {
  incoming_conversations = 'Incoming Conversations',
  outgoing_conversations = 'Outgoing Conversations',
  other_triggers = 'Other Triggers',
}

export enum TriggerType {
  whatsapp = 'whatsapp',
  message = 'message',
  call = 'call',
  chat = 'chat',
  http = 'http',
  outbound_whatsapp = 'outbound_whatsapp',
  outbound_message = 'outbound_message',
  outbound_call = 'outbound_call',
  incoming_slack = 'incoming_slack',
}

export enum AgentChannels {
  message = 'message',
  whatsapp = 'whatsapp',
  chat = 'chat',
  call = 'call',
  slack = 'slack',
}

export const SlackTriggerEventText = {
  on_mention_in_channel: {
    title: 'On mention in channel',
    description: 'Triggers when bot is mentioned in a specific channel',
  },
  on_direct_mention: {
    title: 'On direct mention',
    description: 'Triggers when someone mentions you in a message',
  },
} as const

export const FlowSourceTypes: Record<FlowTypes, string> = {
  [FlowTypes.incoming_call]: 'call',
  [FlowTypes.incoming_chat]: 'chat',
  [FlowTypes.incoming_sms]: 'message',
  [FlowTypes.incoming_whatsapp]: 'whatsapp',
  [FlowTypes.outgoing_call]: 'outbound_call',
  [FlowTypes.outgoing_sms]: 'outbound_message',
  [FlowTypes.outgoing_whatsapp]: 'outbound_whatsapp',
  [FlowTypes.api_request]: 'http',
  [FlowTypes.incoming_slack]: 'incoming_slack',
}

export const FlowSourceTypeToTriggerTitle: Record<string, string> = {
  [FlowSourceTypes[FlowTypes.incoming_call]]: 'Voice Call',
  [FlowSourceTypes[FlowTypes.incoming_chat]]: 'Chat Message',
  [FlowSourceTypes[FlowTypes.incoming_sms]]: 'SMS',
  [FlowSourceTypes[FlowTypes.incoming_whatsapp]]: 'WhatsApp Message',
  [FlowSourceTypes[FlowTypes.outgoing_call]]: 'When a call is made',
  [FlowSourceTypes[FlowTypes.outgoing_sms]]: 'When a SMS is sent',
  [FlowSourceTypes[FlowTypes.outgoing_whatsapp]]: 'When a WhatsApp message is sent',
  [FlowSourceTypes[FlowTypes.api_request]]: 'API Request',
  [FlowSourceTypes[FlowTypes.incoming_slack]]: 'Slack',
}

// Shared worker message types
export enum WorkerMessageTypes {
  ping = 'ping',
  setAuth = 'setAuth',
  start = 'start',
  stop = 'stop',
  startTabHeartbeat = 'startTabHeartbeat',
  stopTabHeartbeat = 'stopTabHeartbeat',
  tabHeartbeat = 'tabHeartbeat',
  logout = 'logout',
}

export interface WorkerMessagePayload {
  [WorkerMessageTypes.ping]: { sdk_heartbeat_ts: string }
  [WorkerMessageTypes.setAuth]: { auth: RUserSession }
  [WorkerMessageTypes.start]: undefined
  [WorkerMessageTypes.stop]: undefined
  [WorkerMessageTypes.startTabHeartbeat]: undefined
  [WorkerMessageTypes.stopTabHeartbeat]: undefined
  [WorkerMessageTypes.tabHeartbeat]: undefined
  [WorkerMessageTypes.logout]: undefined
}

export interface IWorkerMessage<TType extends WorkerMessageTypes> {
  type: TType
  data: WorkerMessagePayload[TType]
}

export enum ConversationsViewType {
  table = 'table',
  detail = 'detail',
}

export interface ConversationMeta {
  error: {
    message?: string
    type: 'Whole conversation' | 'Specific message' // Blocks the whole input field if type is 'Whole conversation'
    channel: Channels
    timeout?: number
  } | null
  templates: Record<string, RWhatsappTemplate>
}

export enum Views {
  active = 'active',
  unassigned = 'unassigned',
  monitoring = 'monitoring',
  records = 'records',

  // Task views
  tasks = 'tasks',
  myTasks = 'my-tasks',
  live = 'live-conversations',
  // The below constant is just the name of the view, and not the url, like the others.
  // the actual url of all conversations is based on the view uuid, coming from the BE
  all_conversations = 'all conversations',
}

export enum TaskViewName {
  AllTasks = 'all_tasks',
  MyTasks = 'my_tasks',
  Unassigned = 'unassigned',
  Exceptions = 'exceptions',
}

export const TaskViewLabels = {
  [TaskViewName.AllTasks]: 'Tasks',
  [TaskViewName.MyTasks]: 'My Tasks',
  [TaskViewName.Unassigned]: 'Unassigned Tasks',
  [TaskViewName.Exceptions]: 'Exception Tasks',
}

export interface RPhloPlaygroundInit {
  flow_type: string
  sip_uri?: string
  flow_uuid: string
  auth_id: string
  chat_data?: RChatData
}

export interface RChatData {
  channelName: string
  contactIdentifier: string
  grant_token: string
}
